import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { T_STRING, T_NUMBER, arrayOf, recordOf, tupleOf } from "@features/flows/utils/types";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";

type Props = {
  mode?: "fromEntries" | "entries";
  valueType?: "string" | "number";
  applyInference?: boolean;
};

function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  // Object • fromEntries
  map.set("core.object.fromEntries", {
    key: "core.object.fromEntries",
    title: "Object • fromEntries",
    category: "Object",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "items", type: "array" }], out: [{ id: "object", type: "object" }] },
    },
    pkg: "core",
    typeLinks: [
      { from: { io: "in", id: "items", op: ["element", { name: "tupleAt", index: 1 }, "recordOf"] }, to: { io: "out", id: "object" } },
    ],
  } as any);
  // Object • entries
  map.set("core.object.entries", {
    key: "core.object.entries",
    title: "Object • entries",
    category: "Object",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "object", type: "object" }], out: [{ id: "items", type: "array" }] },
    },
    pkg: "core",
    typeLinks: [
      { from: { io: "in", id: "object", op: ["value", "entriesOf", "arrayOf"] }, to: { io: "out", id: "items" } },
    ],
  } as any);
  return map;
}

function TupleOpsPreview({ mode = "fromEntries", valueType = "string", applyInference = true }: Props) {
  const templates = React.useMemo(buildTemplates, []);
  const valExpr = valueType === "number" ? T_NUMBER : T_STRING;

  const isFromEntries = mode === "fromEntries";
  const baseNodes: Node[] = isFromEntries
    ? [
        // Source: array<[string, T]>
        {
          id: "src",
          type: "default",
          position: { x: 80, y: 120 },
          data: {
            label: `Source (Array<[string, ${valueType}]>)`,
            pins: {
              exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
              data: { in: [], out: [{ id: "items", type: "array", typeExpr: arrayOf(tupleOf(T_STRING, valExpr)) }] },
            },
          },
        },
        // fromEntries node
        {
          id: "op",
          type: "default",
          position: { x: 420, y: 120 },
          data: {
            label: "Object • fromEntries",
            templateKey: "core.object.fromEntries",
            pins: {
              exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
              data: { in: [{ id: "items", type: "array" }], out: [{ id: "object", type: "object" }] },
            },
          },
        },
      ]
    : [
        // Source: Record<string, T>
        {
          id: "src",
          type: "default",
          position: { x: 80, y: 120 },
          data: {
            label: `Source (Record<string, ${valueType}>)`,
            pins: {
              exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
              data: { in: [], out: [{ id: "object", type: "object", typeExpr: recordOf(T_STRING, valExpr) }] },
            },
          },
        },
        // entries node
        {
          id: "op",
          type: "default",
          position: { x: 420, y: 120 },
          data: {
            label: "Object • entries",
            templateKey: "core.object.entries",
            pins: {
              exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
              data: { in: [{ id: "object", type: "object" }], out: [{ id: "items", type: "array" }] },
            },
          },
        },
      ];

  const baseEdges: Edge[] = [
    {
      id: "e",
      type: "data",
      source: "src",
      target: "op",
      sourceHandle: `data:out:${isFromEntries ? "items" : "object"}`,
      targetHandle: `data:in:${isFromEntries ? "items" : "object"}`,
      data: { channel: "data", rawSourceHandle: isFromEntries ? "items" : "object", rawTargetHandle: isFromEntries ? "items" : "object" },
    } as any,
  ];

  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edges, setEdges] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    if (!applyInference) { setNodes(baseNodes); setEdges(baseEdges); return; }
    try {
      const inferred = inferTypes(baseNodes as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(baseNodes as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdges(baseEdges);
    } catch {
      setNodes(baseNodes);
      setEdges(baseEdges);
    }
  }, [applyInference, valueType, mode, templates]);

  return (
    <div className="h-[560px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/TupleOps",
  title: "Tuple Ops (tupleAt, entriesOf)",
  group: "Flows/Typing",
  component: TupleOpsPreview as any,
  description: "Demonstrates tupleAt and entriesOf in typeLinks for fromEntries and entries.",
  defaultProps: { mode: "fromEntries", valueType: "string", applyInference: true },
  controls: {
    mode: { type: "select", label: "Mode", options: ["fromEntries", "entries"] },
    valueType: { type: "select", label: "Value Type", options: ["string", "number"] },
    applyInference: { type: "boolean", label: "Apply Inference" },
  },
  variants: [
    { name: "fromEntries (string)", props: { mode: "fromEntries", valueType: "string", applyInference: true } },
    { name: "fromEntries (number)", props: { mode: "fromEntries", valueType: "number", applyInference: true } },
    { name: "entries (string)", props: { mode: "entries", valueType: "string", applyInference: true } },
    { name: "entries (number)", props: { mode: "entries", valueType: "number", applyInference: true } },
    { name: "Before (no inference)", props: { applyInference: false } },
  ],
};

export default meta;

